type Categorizable = { category?: string; name: string };
export type VendorCategoryGroupMode = "consumer" | "hr";

// Category groupings as specified in the requirements
export const VENDOR_CATEGORY_GROUPS = {
  SALES_MARKETING: {
    name: "Sales/Marketing",
    order: 1,
    categories: [
      "Ad Network",
      "Sales & Marketing Tool",
      "Data Analytics Provider",
      "Data Broker",
      "Data Cooperative",
    ],
  },
  CUSTOMER_PURCHASES: {
    name: "Customer Purchases",
    order: 2,
    categories: ["Commerce Software Tool", "Payment Processor", "Shipping Service"],
  },
  CUSTOMER_SUPPORT: {
    name: "Customer Support",
    order: 3,
    categories: ["Customer Support Tool"],
  },
  BUSINESS_OPERATIONS: {
    name: "Business Operations",
    order: 4,
    categories: [
      "Business Operations Tool",
      "Cloud Computing & Storage Provider",
      "Collaboration & Productivity Tool",
      "Communications Tool",
      "IT Infrastructure Service",
      "Web Hosting Service",
    ],
  },
  MISCELLANEOUS: {
    name: "Miscellaneous/Other",
    order: 5,
    categories: [], // This will catch all uncategorized vendors
  },
};

export const HR_VENDOR_CATEGORY_GROUPS = {
  HR_OPERATIONS_TOOLS: {
    name: "HR Operations Tools",
    order: 1,
    categories: [
      "HR Software",
      "Payroll & Benefits Management Software",
      "Workforce Management Software",
      "Employee Time-Tracking Software",
    ],
  },
  TALENT_ACQUISITION: {
    name: "Talent Acquisition",
    order: 2,
    categories: [
      "Recruitment & Applicant Tracking System",
      "Recruitment Service",
      "Background Check Provider",
    ],
  },
  LEARNING_PERFORMANCE: {
    name: "Learning & Performance",
    order: 3,
    categories: ["Learning & Performance Management Software"],
  },
  HR_CONTRACTORS: {
    name: "HR Contractors",
    order: 4,
    categories: ["Human Resources Contractors"],
  },
  MISCELLANEOUS: {
    name: "Miscellaneous/Other",
    order: 5,
    categories: [],
  },
};

const getCategoryGroups = (mode: VendorCategoryGroupMode) =>
  mode === "hr" ? HR_VENDOR_CATEGORY_GROUPS : VENDOR_CATEGORY_GROUPS;

/**
 * Maps a vendor category to its category group
 */
export const getCategoryGroup = (
  vendorCategory: string | null | undefined,
  mode: VendorCategoryGroupMode = "consumer",
): string => {
  const categoryGroups = getCategoryGroups(mode);

  if (!vendorCategory) {
    return categoryGroups.MISCELLANEOUS.name;
  }

  // Check each category group for a match
  for (const group of Object.values(categoryGroups)) {
    if (group.categories.includes(vendorCategory)) {
      return group.name;
    }
  }

  // If no match found, put in miscellaneous
  return categoryGroups.MISCELLANEOUS.name;
};

/**
 * Gets the sort order for a category group
 */
export const getCategoryGroupOrder = (
  groupName: string,
  mode: VendorCategoryGroupMode = "consumer",
): number => {
  const group = Object.values(getCategoryGroups(mode)).find((g) => g.name === groupName);
  return group?.order ?? 999;
};

/**
 * Groups vendors by category and sorts them appropriately
 */
export const groupAndSortVendorsByCategory = <T extends Categorizable>(
  vendors: T[],
  mode: VendorCategoryGroupMode = "consumer",
): {
  [categoryGroup: string]: T[];
} => {
  if (!vendors) {
    return {};
  }

  const groupedVendors: { [categoryGroup: string]: T[] } = {};

  vendors.forEach((vendor) => {
    const categoryGroup = getCategoryGroup(vendor.category, mode);
    if (!groupedVendors[categoryGroup]) {
      groupedVendors[categoryGroup] = [];
    }
    groupedVendors[categoryGroup].push(vendor);
  });

  Object.keys(groupedVendors).forEach((group) => {
    groupedVendors[group].sort((a, b) => a.name.localeCompare(b.name));
  });

  return groupedVendors;
};

/**
 * Sorts vendors by category groups, then alphabetically within each group
 * Returns a flat array of vendors sorted by category group order
 */
export const sortVendorsByCategory = <T extends Categorizable>(vendors: T[]): T[] => {
  if (!vendors) {
    return [];
  }

  const groupedVendors = groupAndSortVendorsByCategory(vendors);
  const sortedGroupNames = Object.keys(groupedVendors).sort((a, b) => {
    return getCategoryGroupOrder(a) - getCategoryGroupOrder(b);
  });

  const result: T[] = [];
  sortedGroupNames.forEach((groupName) => {
    result.push(...groupedVendors[groupName]);
  });

  return result;
};
